import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';

import {ReportTreeValue} from '../../../+reports/reports.interfaces';
import {ModalService} from '../../modal';
import {DropdownTreeviewComponent, DropdownTreeviewConfig} from '../tree-multi-select/dropdown-treeview.component';
import {TreeItem} from '../tree-multi-select/treeview.component';

export type AddFieldsTarget = 'filter' | 'column' | 'both';

export interface AddFieldsSelection {
  fields: ReportTreeValue[];
  target: AddFieldsTarget;
}

@Component({
  selector: 'gp-add-fields-modal',
  templateUrl: './add-fields-modal.component.html',
  styleUrls: ['./add-fields-modal.component.scss']
})
export class AddFieldsModalComponent {
  @Input() public fieldsTree: TreeItem[] = [];
  @Input() public loading = false;
  @Input() public currentFilterCount = 0;
  @Input() public currentColumnCount = 0;
  @Input() public maxFilters = 10;
  @Input() public maxColumns = 100;
  @Input() public targets: AddFieldsTarget[] = ['filter', 'column', 'both'];
  @Input() public titleKey = 'REPORTS.select_fields_for_report';
  @Input() public filterWarningKey = 'REPORTS.too_much_fields_filters';
  @Input() public modalId = 'add-fields';

  @Output() public fieldsAdded = new EventEmitter<AddFieldsSelection>();

  @ViewChild(DropdownTreeviewComponent) private fieldTreeView: DropdownTreeviewComponent;

  public selectedFields: ReportTreeValue[] = [];
  public readonly treeConfig: DropdownTreeviewConfig = {
    isShowFilter: true,
    isShowCollapseExpand: true
  };

  constructor(private modalService: ModalService) {}

  public get isSingleTarget(): boolean {
    return this.targets.length === 1;
  }

  public get showFilterTarget(): boolean {
    return this.targets.includes('filter') || this.targets.includes('both');
  }

  public get showColumnTarget(): boolean {
    return this.targets.includes('column') || this.targets.includes('both');
  }

  public get showBothTarget(): boolean {
    return this.targets.includes('both');
  }

  public get filterWouldExceed(): boolean {
    return this.currentFilterCount + this.selectedFields.length > this.maxFilters;
  }

  public get columnWouldExceed(): boolean {
    return this.currentColumnCount + this.selectedFields.length > this.maxColumns;
  }

  public get limitWarningKey(): string {
    const filterExceeded = this.showFilterTarget && this.filterWouldExceed;
    const columnExceeded = this.showColumnTarget && this.columnWouldExceed;
    if (filterExceeded && columnExceeded) {
      return 'REPORTS.too_much_fields_results_filters';
    }
    if (columnExceeded) {
      return 'REPORTS.too_much_fields_results';
    }
    if (filterExceeded) {
      return this.filterWarningKey;
    }
    return null;
  }

  public open(): void {
    this.selectedFields = [];
    this.fieldTreeView?.resetDropdown();
    this.modalService.openModalWithId(this.modalId);
  }

  public close(): void {
    this.modalService.modalClosed.emit(this.modalId);
  }

  public onSelectionChange(selected: ReportTreeValue[]): void {
    this.selectedFields = selected;
  }

  public canAdd(target: AddFieldsTarget): boolean {
    if (!this.selectedFields.length) {
      return false;
    }
    if (target === 'filter') {
      return !this.filterWouldExceed;
    }
    if (target === 'column') {
      return !this.columnWouldExceed;
    }
    return !this.filterWouldExceed && !this.columnWouldExceed;
  }

  public addFields(target: AddFieldsTarget): void {
    if (!this.canAdd(target)) {
      return;
    }
    this.fieldsAdded.emit({fields: this.selectedFields, target});
    this.close();
  }
}
